<template>
    <section class="recommendation-card recommendation-card-private">
        <div class="recommendation-card__header">
            <div>
                <p class="recommendation-card__eyebrow">{{ labels.privateEyebrow }}</p>
                <h3 class="recommendation-card__title">{{ labels.privateTitle }}</h3>
                <p class="recommendation-card__subtitle">{{ labels.privateSubtitle }}</p>
            </div>
            <div class="recommendation-card__header-actions">
                <span class="recommendation-card__badge">{{ versionLabel }}</span>
                <button type="button" class="btn btn-primary btn-sm" @click="startCreate">
                    {{ formVisible ? labels.cancel : labels.newRecommendation }}
                </button>
            </div>
        </div>

        <div v-if="error" class="alert alert-danger py-2 mb-3">{{ error }}</div>

        <div v-if="formVisible" class="recommendation-editor mb-4">
            <div class="row g-3">
                <div class="col-12 col-md-4">
                    <label class="form-label">{{ labels.type }}</label>
                    <select class="form-select" v-model="form.recommendation_type">
                        <option value="new_trick">{{ labels.newType }}</option>
                        <option value="edit_existing_trick">{{ labels.editType }}</option>
                    </select>
                </div>
                <div v-if="form.recommendation_type === 'edit_existing_trick'" class="col-12 col-md-8">
                    <label class="form-label">{{ labels.targetTrick }}</label>
                    <div class="recommendation-trick-picker">
                        <div class="input-group">
                            <input
                                ref="trickSearchInput"
                                type="text"
                                class="form-control"
                                v-model.trim="trickSearchQuery"
                                :placeholder="labels.searchTrick"
                            />
                            <button
                                v-if="selectedTrick"
                                type="button"
                                class="btn btn-outline-secondary"
                                @click="clearSelectedTrick"
                            >
                                {{ labels.clearSelection }}
                            </button>
                        </div>
                        <div v-if="selectedTrick" class="recommendation-trick-picker__selected">
                            <span class="recommendation-chip">{{ labels.selectedTrickLabel }}</span>
                            <strong>#{{ selectedTrick.id }} · {{ selectedTrick.name }}</strong>
                            <span v-if="selectedTrick.short_name" class="recommendation-chip recommendation-chip-muted">{{ selectedTrick.short_name }}</span>
                        </div>
                        <div v-if="showTrickResults" class="recommendation-trick-picker__results">
                            <button
                                v-for="trick in visibleFilteredTricks"
                                :key="trick.id"
                                type="button"
                                class="recommendation-trick-picker__result"
                                :class="{ 'is-active': Number(form.trick_id) === Number(trick.id) }"
                                @click="handleTrickSelection(trick)"
                            >
                                <span class="recommendation-trick-picker__result-id">#{{ trick.id }}</span>
                                <span class="recommendation-trick-picker__result-main">
                                    <strong>{{ trick.name }}</strong>
                                    <small v-if="trick.short_name">{{ trick.short_name }}</small>
                                </span>
                            </button>
                            <small class="form-text text-muted">{{ filteredTricks.length }} {{ labels.filteredResults }}</small>
                        </div>
                        <div v-else-if="showNoTrickResults" class="recommendation-trick-picker__empty">
                            {{ labels.noTrickResults }}
                        </div>
                    </div>
                </div>
                <div class="col-12 col-md-6">
                    <label class="form-label">{{ labels.name }}</label>
                    <input type="text" class="form-control" v-model="form.proposed_name" />
                </div>
                <div class="col-12 col-md-6">
                    <label class="form-label">{{ labels.shortName }}</label>
                    <input type="text" class="form-control" v-model="form.proposed_short_name" />
                </div>
                <div class="col-12">
                    <label class="form-label">{{ labels.description }}</label>
                    <textarea class="form-control" rows="2" v-model="form.proposed_description"></textarea>
                </div>
                <div class="col-12 col-md-3">
                    <label class="form-label">{{ labels.category }}</label>
                    <select class="form-select" v-model="form.proposed_categorie_id">
                        <option :value="null">{{ labels.notSpecified }}</option>
                        <option v-for="category in categories" :key="category.id" :value="category.id">{{ category.name }}</option>
                    </select>
                </div>
                <div class="col-12 col-md-3">
                    <label class="form-label">{{ labels.level }}</label>
                    <select class="form-select" v-model="form.proposed_level_id">
                        <option :value="null">{{ labels.notSpecified }}</option>
                        <option v-for="level in levels" :key="level.id" :value="level.id">{{ level.name }}</option>
                    </select>
                </div>
                <div class="col-12 col-md-3">
                    <label class="form-label">{{ labels.endType }}</label>
                    <select class="form-select" v-model="form.proposed_type_id">
                        <option :value="null">{{ labels.notSpecified }}</option>
                        <option v-for="type in types" :key="type.id" :value="type.id">{{ type.name }}</option>
                    </select>
                </div>
                <div class="col-12 col-md-3">
                    <label class="form-label">{{ labels.value }}</label>
                    <input type="number" min="0" step="0.001" class="form-control" v-model.number="form.proposed_value" />
                </div>
                <div class="col-12">
                    <label class="form-label">{{ labels.detail }}</label>
                    <textarea class="form-control" rows="4" v-model="form.detail"></textarea>
                </div>
            </div>

            <div class="recommendation-editor__actions mt-3">
                <button type="button" class="btn btn-outline-secondary" @click="resetForm">{{ labels.cancel }}</button>
                <button type="button" class="btn btn-primary" :disabled="saving" @click="submit">
                    {{ saving ? labels.saving : labels.save }}
                </button>
            </div>
        </div>

        <div class="recommendation-stats row g-3 mb-4">
            <div class="col-12 col-md-4">
                <div class="recommendation-stat-box">
                    <span>{{ labels.totalMine }}</span>
                    <strong>{{ recommendations.length }}</strong>
                </div>
            </div>
            <div class="col-12 col-md-4">
                <div class="recommendation-stat-box">
                    <span>{{ labels.newType }}</span>
                    <strong>{{ typeCount('new_trick') }}</strong>
                </div>
            </div>
            <div class="col-12 col-md-4">
                <div class="recommendation-stat-box">
                    <span>{{ labels.editType }}</span>
                    <strong>{{ typeCount('edit_existing_trick') }}</strong>
                </div>
            </div>
        </div>

        <div v-if="loading" class="recommendation-empty">{{ labels.loading }}</div>
        <div v-else-if="recommendations.length === 0" class="recommendation-empty">{{ labels.privateEmpty }}</div>
        <div v-else class="recommendation-list">
            <article v-for="recommendation in recommendations" :key="recommendation.id" class="recommendation-item">
                <div class="recommendation-item__top">
                    <div>
                        <div class="recommendation-item__meta">
                            <span class="recommendation-chip">{{ formatType(recommendation.recommendation_type) }}</span>
                        </div>
                        <h4 class="recommendation-item__title">
                            {{ recommendation.proposed_name || recommendation.trick?.name || labels.untitled }}
                        </h4>
                    </div>
                    <div class="recommendation-item__actions">
                        <button type="button" class="btn btn-outline-primary btn-sm" @click="editRecommendation(recommendation)">{{ labels.edit }}</button>
                        <button type="button" class="btn btn-outline-danger btn-sm" :disabled="deletingId === recommendation.id" @click="$emit('delete', recommendation.id)">{{ deletingId === recommendation.id ? labels.deleting : labels.delete }}</button>
                    </div>
                </div>
                <p class="recommendation-item__detail">{{ recommendation.detail }}</p>
                <dl class="recommendation-item__grid">
                    <div>
                        <dt>{{ labels.category }}</dt>
                        <dd>{{ recommendation.proposed_category?.name || labels.notSpecified }}</dd>
                    </div>
                    <div>
                        <dt>{{ labels.level }}</dt>
                        <dd>{{ recommendation.proposed_level?.name || labels.notSpecified }}</dd>
                    </div>
                    <div>
                        <dt>{{ labels.value }}</dt>
                        <dd>{{ recommendation.proposed_value ?? labels.notSpecified }}</dd>
                    </div>
                    <div>
                        <dt>{{ labels.updatedAt }}</dt>
                        <dd>{{ formatDate(recommendation.updated_at) }}</dd>
                    </div>
                </dl>
            </article>
        </div>
    </section>
</template>

<script>
export default {
    name: 'TrickRecommendationPrivateCard',
    props: {
        recommendations: { type: Array, default: () => [] },
        tricklistVersion: { type: Object, default: null },
        tricks: { type: Array, default: () => [] },
        categories: { type: Array, default: () => [] },
        levels: { type: Array, default: () => [] },
        types: { type: Array, default: () => [] },
        loading: { type: Boolean, default: false },
        saving: { type: Boolean, default: false },
        error: { type: String, default: '' },
        deletingId: { type: Number, default: null },
        labels: { type: Object, required: true },
    },
    data() {
        return {
            formVisible: false,
            editingId: null,
            form: this.buildEmptyForm(),
            trickSearchQuery: '',
        };
    },
    computed: {
        versionLabel() {
            if (!this.tricklistVersion) {
                return this.labels.unresolvedVersion;
            }

            return this.tricklistVersion.name
                ? `${this.labels.version}: ${this.tricklistVersion.name}`
                : `${this.labels.version}: #${this.tricklistVersion.id}`;
        },
        filteredTricks() {
            const query = this.normalizedTrickSearchQuery;
            if (!query) {
                return this.tricks;
            }

            return this.tricks.filter((trick) => {
                const id = String(trick.id || '');
                const name = String(trick.name || '').toLowerCase();
                const shortName = String(trick.short_name || '').toLowerCase();
                return id.includes(query) || name.includes(query) || shortName.includes(query);
            });
        },
        normalizedTrickSearchQuery() {
            return String(this.trickSearchQuery || '').toLowerCase().trim();
        },
        selectedTrick() {
            if (!this.form.trick_id) {
                return null;
            }

            return this.tricks.find((entry) => Number(entry.id) === Number(this.form.trick_id)) || null;
        },
        visibleFilteredTricks() {
            const matches = this.filteredTricks;
            if (!this.normalizedTrickSearchQuery) {
                return this.selectedTrick ? [this.selectedTrick] : [];
            }

            return matches.slice(0, 8);
        },
        showTrickResults() {
            return !this.selectedTrick && this.visibleFilteredTricks.length > 0;
        },
        showNoTrickResults() {
            return !this.selectedTrick && Boolean(this.normalizedTrickSearchQuery) && this.filteredTricks.length === 0;
        },
    },
    methods: {
        buildEmptyForm() {
            return {
                recommendation_type: 'new_trick',
                trick_id: null,
                proposed_name: '',
                proposed_short_name: '',
                proposed_description: '',
                proposed_categorie_id: null,
                proposed_level_id: null,
                proposed_type_id: null,
                proposed_value: null,
                detail: '',
            };
        },
        startCreate() {
            if (this.formVisible && !this.editingId) {
                this.resetForm();
                return;
            }

            this.formVisible = true;
            this.editingId = null;
            this.form = this.buildEmptyForm();
            this.trickSearchQuery = '';
            this.focusTrickSearch();
        },
        resetForm() {
            this.formVisible = false;
            this.editingId = null;
            this.form = this.buildEmptyForm();
            this.trickSearchQuery = '';
        },
        focusTrickSearch() {
            this.$nextTick(() => {
                this.$refs.trickSearchInput?.focus?.();
            });
        },
        applyTrickToForm(trick) {
            if (!trick) {
                return;
            }

            this.form = {
                ...this.form,
                trick_id: trick.id ?? null,
                proposed_name: trick.name || '',
                proposed_short_name: trick.short_name || '',
                proposed_description: trick.description || '',
                proposed_categorie_id: trick.categorie_id ?? null,
                proposed_level_id: trick.level_id ?? null,
                proposed_type_id: trick.type_id ?? null,
                proposed_value: trick.value ?? null,
            };
            this.trickSearchQuery = trick.name || '';
        },
        openForExistingTrick(trick) {
            this.formVisible = true;
            this.editingId = null;
            this.form = {
                ...this.buildEmptyForm(),
                recommendation_type: 'edit_existing_trick',
            };
            this.applyTrickToForm(trick);
            this.focusTrickSearch();
        },
        clearSelectedTrick() {
            this.form = {
                ...this.form,
                trick_id: null,
            };
            this.trickSearchQuery = '';
            this.focusTrickSearch();
        },
        submit() {
            this.$emit('submit', {
                id: this.editingId,
                payload: {
                    ...this.form,
                    proposed_value: this.form.proposed_value === '' ? null : this.form.proposed_value,
                },
                onSuccess: this.resetForm,
            });
        },
        editRecommendation(recommendation) {
            this.formVisible = true;
            this.editingId = recommendation.id;
            this.form = {
                recommendation_type: recommendation.recommendation_type,
                trick_id: recommendation.trick?.id ?? null,
                proposed_name: recommendation.proposed_name || '',
                proposed_short_name: recommendation.proposed_short_name || '',
                proposed_description: recommendation.proposed_description || '',
                proposed_categorie_id: recommendation.proposed_category?.id ?? null,
                proposed_level_id: recommendation.proposed_level?.id ?? null,
                proposed_type_id: recommendation.proposed_type?.id ?? null,
                proposed_value: recommendation.proposed_value ?? null,
                detail: recommendation.detail || '',
            };
            this.trickSearchQuery = recommendation.trick?.name || recommendation.proposed_name || '';
            this.focusTrickSearch();
        },
        handleTrickSelection(trick) {
            const selectedTrick = trick || this.selectedTrick;
            if (!selectedTrick) {
                return;
            }

            this.applyTrickToForm(selectedTrick);
        },
        typeCount(type) {
            return this.recommendations.filter((recommendation) => recommendation.recommendation_type === type).length;
        },
        formatType(type) {
            return type === 'edit_existing_trick' ? this.labels.editType : this.labels.newType;
        },
        formatDate(value) {
            if (!value) return this.labels.notSpecified;
            const date = new Date(value);
            if (Number.isNaN(date.getTime())) return this.labels.notSpecified;
            return date.toLocaleString();
        },
    },
    watch: {
        'form.recommendation_type'(value) {
            if (value !== 'edit_existing_trick') {
                this.form.trick_id = null;
                this.trickSearchQuery = '';
            } else {
                this.focusTrickSearch();
            }
        },
    },
};
</script>

<style scoped>
.recommendation-card {
    border: 1px solid #e5e7eb;
    border-radius: 18px;
    padding: 20px;
    background: #ffffff;
    box-shadow: 0 14px 34px rgba(15, 23, 42, 0.06);
}

.recommendation-card-private {
    background: linear-gradient(135deg, rgba(244, 247, 255, 0.95), rgba(255, 255, 255, 0.96));
}

.recommendation-card__header {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    align-items: flex-start;
    margin-bottom: 16px;
}

.recommendation-card__eyebrow {
    margin: 0 0 6px;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #4f46e5;
    font-weight: 700;
}

.recommendation-card__title {
    margin: 0;
    font-size: 1.25rem;
}

.recommendation-card__subtitle {
    margin: 6px 0 0;
    color: #64748b;
}

.recommendation-card__header-actions {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
    justify-content: flex-end;
}

.recommendation-card__badge,
.recommendation-chip {
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    padding: 6px 10px;
    font-size: 0.78rem;
    font-weight: 600;
    background: #e2e8f0;
    color: #1e293b;
}

.recommendation-chip-muted {
    background: #f1f5f9;
    color: #475569;
}

.recommendation-editor {
    border-top: 1px solid #e2e8f0;
    padding-top: 18px;
}

.recommendation-editor__actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
}

.recommendation-trick-picker {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.recommendation-trick-picker__selected {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.recommendation-trick-picker__results {
    border: 1px solid #dbe4f0;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.96);
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.recommendation-trick-picker__result {
    border: 1px solid transparent;
    border-radius: 12px;
    background: transparent;
    padding: 10px 12px;
    text-align: left;
    display: flex;
    gap: 12px;
    align-items: center;
    transition: background-color 0.2s ease, border-color 0.2s ease;
}

.recommendation-trick-picker__result:hover,
.recommendation-trick-picker__result.is-active {
    background: #eef4ff;
    border-color: #bfdbfe;
}

.recommendation-trick-picker__result-id {
    min-width: 52px;
    font-weight: 700;
    color: #2563eb;
}

.recommendation-trick-picker__result-main {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.recommendation-trick-picker__result-main strong,
.recommendation-trick-picker__result-main small {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.recommendation-trick-picker__result-main small {
    color: #64748b;
}

.recommendation-trick-picker__empty {
    border: 1px dashed #cbd5e1;
    border-radius: 12px;
    padding: 10px 12px;
    color: #64748b;
    background: #f8fafc;
}

.recommendation-stat-box {
    border: 1px solid #dbeafe;
    border-radius: 14px;
    background: #eff6ff;
    padding: 14px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.recommendation-stat-box span {
    color: #475569;
}

.recommendation-stat-box strong {
    font-size: 1.2rem;
}

.recommendation-empty {
    border: 1px dashed #cbd5e1;
    border-radius: 14px;
    padding: 20px;
    text-align: center;
    color: #64748b;
}

.recommendation-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-height: 680px;
    overflow-y: auto;
    padding-right: 6px;
    scrollbar-gutter: stable;
}

.recommendation-item {
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    padding: 16px;
    background: rgba(255, 255, 255, 0.9);
}

.recommendation-item__top {
    display: flex;
    justify-content: space-between;
    gap: 12px;
}

.recommendation-item__meta {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 8px;
}

.recommendation-item__title {
    margin: 0;
    font-size: 1rem;
}

.recommendation-item__detail {
    margin: 12px 0;
    color: #334155;
}

.recommendation-item__actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.recommendation-item__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 12px;
    margin: 0;
}

.recommendation-item__grid dt {
    font-size: 0.78rem;
    color: #64748b;
}

.recommendation-item__grid dd {
    margin: 4px 0 0;
    font-weight: 600;
}

@media (max-width: 767px) {
    .recommendation-card__header,
    .recommendation-item__top {
        flex-direction: column;
    }
}
</style>